import { ContactSubjects, ISocialProfile } from "./../interface/cms.interface";
import { PrivacyPolicy } from "./../../components/page/privacy-policy/privacy-policy";
import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { tap } from "rxjs";
import {
  IBranch,
  IHtmlContent,
  ITestimonial,
} from "../interface/cms.interface";
import { CmsService } from "../services/cms.service";
import {
  GetAboutAction,
  GetBannerAction,
  GetBranchAction,
  GetContactSubjectAction,
  GetFAQAction,
  GetNoticeAction,
  GetPrivacyPolicyAction,
  GetServiesAction,
  GetSocialProfileAction,
  GetTestimonialAction,
  PostContactAction,
} from "../action/cms.action";

export class CmsStateModel {
  about: IHtmlContent | null;
  privacy_policy: IHtmlContent | null;
  services: IHtmlContent[];
  testimonials: ITestimonial[];
  branches: IBranch[];
  faq: IHtmlContent[];
  contactSubjects: ContactSubjects[];
  banners: IHtmlContent[];
  socialProfile: ISocialProfile | null;
  notices: IHtmlContent[];
}

@State<CmsStateModel>({
  name: "cms",
  defaults: {
    about: null,
    privacy_policy: null,
    services: [],
    testimonials: [],
    branches: [],
    faq: [],
    contactSubjects: [],
    banners: [],
    socialProfile: null,
    notices: [],
  },
})
@Injectable()
export class CmsState {
  private cmsService = inject(CmsService);

  @Selector()
  static about(state: CmsStateModel) {
    return state.about;
  }

  @Selector()
  static services(state: CmsStateModel) {
    return state.services;
  }

  @Selector()
  static testimoials(state: CmsStateModel) {
    return state.testimonials;
  }

  @Selector()
  static branches(state: CmsStateModel) {
    return state.branches;
  }

  @Selector()
  static faq(state: CmsStateModel) {
    return state.faq;
  }

  @Selector()
  static privacy_policy(state: CmsStateModel) {
    return state.privacy_policy;
  }

  @Selector()
  static contact_subjects(state: CmsStateModel) {
    return state.contactSubjects;
  }

  @Selector()
  static banners(state: CmsStateModel) {
    return state.banners;
  }

  @Selector()
  static socialProfile(state: CmsStateModel) {
    return state.socialProfile;
  }

  @Selector()
  static notices(state: CmsStateModel) {
    return state.notices;
  }

  @Action(GetAboutAction)
  getAbout(ctx: StateContext<CmsStateModel>, action: GetAboutAction) {
    this.cmsService.skeletonLoader = true;
    return this.cmsService
      .SingleContent(
        action.payload!["masterId"],
        action.payload!["contentId"],
        action.payload!["cTypeId"]
      )
      .pipe(
        tap({
          next: (result) => {
            ctx.patchState({
              about: result.data,
            });
          },
          complete: () => {
            this.cmsService.skeletonLoader = false;
          },
          error: (err) => {
            throw new Error(err?.error?.message);
          },
        })
      );
  }

  @Action(GetServiesAction)
  getServices(ctx: StateContext<CmsStateModel>, action: GetServiesAction) {
    this.cmsService.skeletonLoader = true;
    return this.cmsService
      .ListofContent(action.payload!["masterId"], action.payload!["cTypeId"])
      .pipe(
        tap({
          next: (result) => {
            ctx.patchState({
              services: result.data,
            });
          },
          complete: () => {
            this.cmsService.skeletonLoader = false;
          },
          error: (err) => {
            throw new Error(err?.error?.message);
          },
        })
      );
  }

  @Action(GetTestimonialAction)
  getTestimonials(
    ctx: StateContext<CmsStateModel>,
    action: GetTestimonialAction
  ) {
    this.cmsService.skeletonLoader = true;
    return this.cmsService.getTestimonials().pipe(
      tap({
        next: (result) => {
          ctx.patchState({
            testimonials: result.data,
          });
        },
        complete: () => {
          this.cmsService.skeletonLoader = false;
        },
        error: (err) => {
          throw new Error(err?.error?.message);
        },
      })
    );
  }

  @Action(GetBranchAction)
  getBranches(ctx: StateContext<CmsStateModel>, action: GetBranchAction) {
    this.cmsService.skeletonLoader = true;
    return this.cmsService.getOffices().pipe(
      tap({
        next: (result) => {
          ctx.patchState({
            branches: result.data,
          });
        },
        complete: () => {
          this.cmsService.skeletonLoader = false;
        },
        error: (err) => {
          throw new Error(err?.error?.message);
        },
      })
    );
  }

  @Action(GetFAQAction)
  getFAQ(ctx: StateContext<CmsStateModel>, action: GetFAQAction) {
    this.cmsService.skeletonLoader = true;
    return this.cmsService
      .ListofContent(action.payload!["masterId"], action.payload!["cTypeId"])
      .pipe(
        tap({
          next: (result) => {
            ctx.patchState({
              faq: result.data,
            });
          },
          complete: () => {
            this.cmsService.skeletonLoader = false;
          },
          error: (err) => {
            throw new Error(err?.error?.message);
          },
        })
      );
  }

  @Action(GetPrivacyPolicyAction)
  getPrivacyPolicy(
    ctx: StateContext<CmsStateModel>,
    action: GetPrivacyPolicyAction
  ) {
    this.cmsService.skeletonLoader = true;
    return this.cmsService
      .SingleContent(
        action.payload!["masterId"],
        action.payload!["contentId"],
        action.payload!["cTypeId"]
      )
      .pipe(
        tap({
          next: (result) => {
            ctx.patchState({
              privacy_policy: result.data,
            });
          },
          complete: () => {
            this.cmsService.skeletonLoader = false;
          },
          error: (err) => {
            throw new Error(err?.error?.message);
          },
        })
      );
  }

  @Action(GetContactSubjectAction)
  getContactSubjects(
    ctx: StateContext<CmsStateModel>,
    action: GetContactSubjectAction
  ) {
    this.cmsService.skeletonLoader = true;
    return this.cmsService.getContactSubjects().pipe(
      tap({
        next: (result) => {
          ctx.patchState({
            contactSubjects: result.data,
          });
        },
        complete: () => {
          this.cmsService.skeletonLoader = false;
        },
        error: (err) => {
          throw new Error(err?.error?.message);
        },
      })
    );
  }

  @Action(GetBannerAction)
  getBanner(ctx: StateContext<CmsStateModel>, action: GetBannerAction) {
    this.cmsService.skeletonLoader = true;
    return this.cmsService
      .ListofContent(action.payload!["masterId"], action.payload!["cTypeId"])
      .pipe(
        tap({
          next: (result) => {
            ctx.patchState({
              banners: result.data,
            });
          },
          complete: () => {
            this.cmsService.skeletonLoader = false;
          },
          error: (err) => {
            throw new Error(err?.error?.message);
          },
        })
      );
  }

  @Action(PostContactAction)
  postContact(ctx: StateContext<CmsStateModel>, action: PostContactAction) {
    this.cmsService.skeletonLoader = true;
    return this.cmsService.saveMessage(action.payload);
  }

  @Action(GetSocialProfileAction)
  GetSocialProfile(
    ctx: StateContext<CmsStateModel>,
    action: GetSocialProfileAction
  ) {
    if (ctx.getState().socialProfile) {
      return;
    }

    return this.cmsService.getSocialProfile().pipe(
      tap((res) => {
        ctx.patchState({ socialProfile: res.data });
      })
    );
  }

  @Action(GetNoticeAction)
  GetNotice(ctx: StateContext<CmsStateModel>, action: GetNoticeAction) {
    if (ctx.getState().notices.length>0) {
      return;
    }

    return this.cmsService
      .ListofContent(action.payload!["masterId"], action.payload!["cTypeId"])
      .pipe(
        tap((res) => {
          ctx.patchState({ notices: res.data });
        })
      );
  }
}
